import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, CheckCircle2, AlertTriangle, Info, Loader2 } from "lucide-react";
import clsx from "clsx";
import { initials } from "@/lib/format";

// ---------- Spinner ----------
export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx("animate-spin", className)} size={16} />;
}

export function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center text-ink-faint">
      <Spinner className="mr-2" /> Loading…
    </div>
  );
}

// ---------- Card ----------
export function Card({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={clsx("card p-4", className)}>{children}</div>;
}

export function StatTile({ label, value, sub, accent }: { label: string; value: React.ReactNode; sub?: React.ReactNode; accent?: string }) {
  return (
    <div className="card p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">{label}</div>
      <div className="mt-1 text-2xl font-semibold" style={accent ? { color: accent } : undefined}>{value}</div>
      {sub != null && <div className="mt-0.5 text-xs text-ink-soft">{sub}</div>}
    </div>
  );
}

// ---------- Avatar ----------
export function Avatar({ name, color, size = 28 }: { name: string; color?: string | null; size?: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{ width: size, height: size, fontSize: size * 0.38, background: color || "rgb(var(--brand-600))" }}
      title={name}
    >
      {initials(name)}
    </span>
  );
}

// ---------- Modal ----------
export function Modal({
  open, onClose, title, children, footer, wide,
}: {
  open: boolean; onClose: () => void; title: React.ReactNode; children: React.ReactNode; footer?: React.ReactNode; wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-[6vh] backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} />
      <div className={clsx("relative w-full animate-fade-in rounded-2xl bg-surface shadow-pop", wide ? "max-w-3xl" : "max-w-lg")}>
        <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <h2 className="font-serif text-lg font-semibold">{title}</h2>
          <button className="btn-ghost -mr-2 !p-1.5" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

// ---------- Confirm dialog hook ----------
export function useConfirm() {
  const [state, setState] = useState<{ title: string; body?: string; danger?: boolean; resolve: (v: boolean) => void } | null>(null);
  const confirm = useCallback((title: string, body?: string, danger?: boolean) =>
    new Promise<boolean>((resolve) => setState({ title, body, danger, resolve })), []);
  const node = state ? (
    <Modal
      open onClose={() => { state.resolve(false); setState(null); }} title={state.title}
      footer={
        <>
          <button className="btn-outline" onClick={() => { state.resolve(false); setState(null); }}>Cancel</button>
          <button className={state.danger ? "btn-danger" : "btn-primary"} onClick={() => { state.resolve(true); setState(null); }}>Confirm</button>
        </>
      }
    >
      <p className="text-sm text-ink-soft">{state.body || "Are you sure?"}</p>
    </Modal>
  ) : null;
  return { confirm, node };
}

// ---------- Toasts ----------
type Toast = { id: number; kind: "success" | "error" | "info"; msg: string };
const ToastCtx = createContext<{ push: (kind: Toast["kind"], msg: string) => void } | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((kind: Toast["kind"], msg: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500);
  }, []);
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-[60] flex w-80 flex-col gap-2">
          {toasts.map((t) => (
            <div key={t.id} className={clsx(
              "flex items-start gap-2.5 rounded-lg border px-3.5 py-3 text-sm shadow-pop animate-fade-in bg-surface",
              t.kind === "success" && "border-st-ontrack/40",
              t.kind === "error" && "border-st-delayed/40",
              t.kind === "info" && "border-line",
            )}>
              {t.kind === "success" && <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-st-ontrack" />}
              {t.kind === "error" && <AlertTriangle size={18} className="mt-0.5 shrink-0 text-st-delayed" />}
              {t.kind === "info" && <Info size={18} className="mt-0.5 shrink-0 text-ink-soft" />}
              <span className="flex-1">{t.msg}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

// ---------- Empty state ----------
export function EmptyState({ icon: Icon, title, hint, action }: { icon?: any; title: string; hint?: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-line py-14 text-center">
      {Icon && <Icon className="mb-3 text-ink-faint" size={28} />}
      <p className="font-medium">{title}</p>
      {hint && <p className="mt-1 max-w-sm text-sm text-ink-faint">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ---------- Progress bar ----------
export function Progress({ value, className }: { value: number; className?: string }) {
  return (
    <div className={clsx("h-1.5 w-full overflow-hidden rounded-full bg-surface-2", className)}>
      <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}
